import {
    PaymentAttemptResponseParams,
    PaymentCategory,
    PaymentProviderType,
    providerTypeFromPaymentCategory,
} from './Payment';
import { useUpdate } from 'dg-web-shared/lib/Flux';
import {
    RequestStatus,
    ServerRequestState,
    ServerWriteState,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { logAction } from '../../utils/ActionLog';
import { UiPaymentOrigin } from 'dg-web-shared/common/utils/TwintPairingParkingpay';
import { useRef } from 'react';
import TwintUoFStartResponse = StoreAliasRequest.TwintUoFStartResponse;
import RedirectStartResponse = StoreAliasRequest.RedirectStartResponse;

export namespace StoreAliasRequest {
    export interface Payload {
        paymentCategory: PaymentCategory;
        uiPaymentOrigin: UiPaymentOrigin;
        useAlias: boolean;
        language: string;
        ticketId?: string;
    }

    export function use(): [
        ServerRequestState<unknown, unknown>,
        (payload: Payload) => void,
        () => void,
    ] {
        const [stateCreditCard, writeCreditCard, resetCreditCard] =
            useCreditCard();
        const [stateTwint, writeTwint, resetTwint] = useTwint();

        return [
            deriveState(stateCreditCard, stateTwint),
            (payload: Payload) => {
                switch (
                    providerTypeFromPaymentCategory(payload.paymentCategory)
                ) {
                    case PaymentProviderType.DATATRANS:
                        return writeCreditCard(payload);
                    case PaymentProviderType.TWINT:
                        return writeTwint(payload);
                }
            },
            () => {
                resetCreditCard();
                resetTwint();
            },
        ];
    }

    const deriveState = (
        stateCreditCard: ServerRequestState<unknown, unknown>,
        stateTwint: ServerRequestState<unknown, unknown>,
    ): ServerRequestState<unknown, unknown> => {
        if (
            stateCreditCard.status === RequestStatus.NEVER_EXECUTED &&
            stateTwint.status === RequestStatus.NEVER_EXECUTED
        ) {
            return { status: RequestStatus.NEVER_EXECUTED, data: null };
        } else if (
            stateCreditCard.status === RequestStatus.PENDING ||
            stateTwint.status === RequestStatus.PENDING
        ) {
            return { status: RequestStatus.PENDING, data: null };
        } else if (
            stateCreditCard.status === RequestStatus.SUCCESS ||
            stateTwint.status === RequestStatus.SUCCESS
        ) {
            return { status: RequestStatus.SUCCESS, data: {} };
        }

        const statusCode =
            stateTwint.status === RequestStatus.ERROR
                ? stateTwint.httpStatusCode
                : stateCreditCard.status === RequestStatus.ERROR
                  ? stateCreditCard.httpStatusCode
                  : 0;

        return {
            status: RequestStatus.ERROR,
            data: null,
            httpStatusCode: statusCode,
        };
    };

    const useCreditCard = (): ServerWriteState<
        PaymentAttemptResponseParams,
        Error,
        Payload
    > => {
        const update = useUpdate();
        type CreditCardRequestPayload = Omit<Payload, 'useAlias'> & {
            baseUri: string;
        };
        const [state, write, reset] = useServerWrite<
            CreditCardRequestPayload,
            PaymentAttemptResponseParams,
            Error
        >(() => ({
            url: '/ui-api/customer-account/deposits/store-alias',
        }));

        useServerSuccessEffect(state, data => {
            if (data.paymentProviderType === PaymentProviderType.DATATRANS) {
                update(store => {
                    logAction(store, 'store-alias-attempt', {
                        ...data,
                    });
                    return 'log-action';
                });

                window.location.href = data.uri;
            }
        });

        return [
            state,
            (p?: Payload) => {
                if (!p) {
                    return;
                }
                write({
                    uiPaymentOrigin: p.uiPaymentOrigin,
                    paymentCategory: p.paymentCategory,
                    baseUri: `${window.location.protocol}//${window.location.host}`,
                    ticketId: p.ticketId,
                    language: p.language,
                });
            },
            reset,
        ];
    };

    export interface TwintUoFStartResponse {
        aliasId: number;
    }

    export interface TokenStartResponse extends TwintUoFStartResponse {
        token: string;
    }

    export interface RedirectStartResponse extends TwintUoFStartResponse {
        twintUrl: string;
    }

    const useTwint = (): ServerWriteState<
        TwintUoFStartResponse,
        Error,
        Payload
    > => {
        const update = useUpdate();
        type TwintRequestPayload = {
            uiPaymentOrigin: UiPaymentOrigin;
            baseUri: string;
            ticketId?: string;
        };
        const payloadRef = useRef<Payload | null>(null);
        const [state, write, reset] = useServerWrite<
            TwintRequestPayload,
            TwintUoFStartResponse,
            Error
        >(() => ({
            url: '/ui-api/customer-account/twint-alias/start-registration',
        }));

        useServerSuccessEffect(state, data => {
            const payload = payloadRef.current;
            if (!payload) {
                return;
            }
            update(store => {
                logAction(store, 'twint-uof-start', {
                    payload,
                });

                if (isRedirectResponse(data)) {
                    window.location.href = data.twintUrl;
                }

                return 'log-action';
            });
        });

        return [
            state,
            (p?: Payload) => {
                if (!p) {
                    return;
                }
                payloadRef.current = p;
                write({
                    uiPaymentOrigin: p.uiPaymentOrigin,
                    baseUri: `${window.location.protocol}//${window.location.host}`,
                    ticketId: p.ticketId,
                });
            },
            reset,
        ];
    };
}

function isRedirectResponse(
    response: TwintUoFStartResponse,
): response is RedirectStartResponse {
    return (response as RedirectStartResponse).twintUrl !== undefined;
}
